import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { Subject } from '../_interfaces/subject';
import { Visit } from '../_interfaces/visit';
import { CreateDictationPayload } from '../_interfaces/dictation';
import { environment } from '../../environments/environment';
import { blankCopilotPatient, formatPatient } from '../_helpers/patient';
import { isScheduleRow, resolveScheduleVisit } from '../_helpers/schedule-visit-location';
import { enrichScheduledSubject } from '../_helpers/subject-enrichment';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService, PatientSelectEntryPoint } from './analytics.service';

@Injectable({
  providedIn: 'root'
})
export class EncounterService {
  private readonly analytics = inject(AnalyticsService);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  async createAndNavigate(
    patient: Subject | { subject_name?: string; subject_id?: string; subject_dob?: string } | null,
    mode: 'consultation' | 'dictation',
    options?: { manual?: boolean; visit?: Visit | null; entryPoint?: PatientSelectEntryPoint }
  ): Promise<void> {
    try {
      const user = this.authService.getUser();
      const dictationData: CreateDictationPayload = {
        application: 'Copilot',
        author: user?.settings.nlpUser || '',
        engine: user?.entity.settings.vr?.nlp_frontend_engine_id || '',
        language: user?.entity.settings.vr?.nlp_frontend_language_id || '',
        topic: user?.entity.settings.vr?.nlp_frontend_topic_id || '',
        type: 'frontend',
        version: environment.version
      };

      const visit = options?.visit ?? null;
      const scheduled = !options?.manual && isScheduleRow(patient) ? patient : null;
      // A schedule row carries no location of its own, so it takes two further lookups to find.
      // Running them alongside the dictation creation keeps them off the critical path.
      const [createRes, scheduleVisit, enrichedPatient] = await Promise.all([
        this.apiService.createDictation(dictationData),
        scheduled && !visit
          ? resolveScheduleVisit(this.apiService, scheduled)
          : Promise.resolve(null),
        scheduled
          ? enrichScheduledSubject(this.apiService, scheduled)
          : Promise.resolve(null),
      ]);
      const id = createRes._id as string;

      if (mode === 'consultation') {
        this.analytics.track('consultation_started', {
          encounter_id: id,
          entry_point: options?.entryPoint,
        });
      } else {
        this.analytics.track('dictation_started', {
          encounter_id: id,
        });
      }

      const dictationType = mode === 'dictation' ? 'dictation' : 'encounter';

      if (patient) {
        const patientFields = formatPatient(patient, {
          manual: options?.manual,
          visit,
          scheduleVisit,
          includeVisitFields: true,
        });
        if (enrichedPatient) {
          patientFields.subject_sex = enrichedPatient.subject_sex?.trim() || null;
          patientFields.subject_death_indicator = enrichedPatient.subject_death_indicator?.trim() || null;
        }
        const payload = {
          copilot: {
            type: dictationType,
            ...patientFields,
          }
        };
        await this.apiService.updateDictationCopilot(id, payload);
      } else {
        const payload = { copilot: blankCopilotPatient(dictationType) };
        await this.apiService.updateDictationCopilot(id, payload);
      }

      if (mode === 'dictation') {
        await this.router.navigate(['/document', id], {
          state: { mode: 'dictation', dictation: createRes }
        });
      } else {
        await this.router.navigate(['/encounter', id]);
      }
    } catch (e) {
      console.error('Error starting flow:', e);
      this.snackBar.open(this.translate.instant('Unable to start encounter.'), this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
      throw e;
    }
  }

  async updatePatient(
    dictationId: string,
    patient: Subject | { subject_name?: string; subject_id?: string; subject_dob?: string; subject_visit_id?: string; subject_alternative_id?: string } | null,
    options?: { manual?: boolean; visit?: Visit | null }
  ): Promise<{ saved: boolean, patient?: any } | undefined> {
    if (!dictationId || !patient) return;

    const hasVisitOption = options != null && 'visit' in options;
    const visit = hasVisitOption ? (options!.visit ?? null) : undefined;
    const scheduled = !options?.manual && isScheduleRow(patient) ? patient : null;
    const [scheduleVisit, enrichedPatient] = await Promise.all([
      hasVisitOption && !visit && scheduled
        ? resolveScheduleVisit(this.apiService, scheduled)
        : Promise.resolve(null),
      scheduled
        ? enrichScheduledSubject(this.apiService, scheduled)
        : Promise.resolve(null),
    ]);

    const patientFields = formatPatient(patient, {
      manual: options?.manual,
      visit,
      scheduleVisit,
      includeVisitFields: hasVisitOption,
    });
    if (enrichedPatient) {
      patientFields.subject_sex = enrichedPatient.subject_sex?.trim() || null;
      patientFields.subject_death_indicator = enrichedPatient.subject_death_indicator?.trim() || null;
    }
    const payload = { copilot: patientFields };

    try {
    await this.apiService.updateDictationCopilot(dictationId, payload);
      const savedPatient = { ...patientFields } as any;
      this.snackBar.open(this.translate.instant('Patient updated successfully.'), this.translate.instant('Close'), { duration: 3000 });
      this.analytics.track('patient_edited', { encounter_id: dictationId });
      return { saved: true, patient: savedPatient };
    } catch (e) {
      this.snackBar.open(this.translate.instant('Error updating patient.'), this.translate.instant('Close'), { duration: 3000 });
      return { saved: false };
    }
  }
}

